import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { 
  CheckCircle2, 
  Target,
  Flame,
  Clock,
  Dumbbell,
  ChevronDown,
  X,
  Zap,
  RotateCcw,
  TrendingUp,
  Circle
} from "lucide-react";
import { cn } from "../../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProgramaPage = () => {
  const { getAuthHeaders } = useAuth();
  const [programData, setProgramData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSession, setExpandedSession] = useState(null);
  const [sessionExercises, setSessionExercises] = useState({});
  const [loadingExercises, setLoadingExercises] = useState({});
  const [previewSession, setPreviewSession] = useState(null);

  useEffect(() => {
    fetchProgram();
    fetchCycleData();
  }, []);

  const fetchProgram = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/workout/program`, {
        headers: getAuthHeaders(),
      });
      setProgramData(response.data);
    } catch (error) {
      console.error("Error fetching program:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCycleData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/workout/week`, {
        headers: getAuthHeaders(),
      });
      // Extract unique workout days from cycle data
      if (response.data.week_data?.days) {
        const trainingSessions = response.data.week_data.days
          .filter(d => !d.is_rest_day)
          .map((d, idx) => ({
            code: d.code,
            name: d.name,
            exercise_count: d.exercise_count
          }));
        setProgramData(prev => prev ? { ...prev, workout_days: trainingSessions } : null);
      }
    } catch (error) {
      console.error("Error fetching cycle data:", error);
    }
  };

  const fetchSessionExercises = async (dayCode) => {
    if (sessionExercises[dayCode] || loadingExercises[dayCode]) return;
    
    setLoadingExercises(prev => ({ ...prev, [dayCode]: true }));
    try {
      const response = await axios.get(`${API_URL}/api/workout/day/${dayCode}`, {
        headers: getAuthHeaders(),
      });
      setSessionExercises(prev => ({
        ...prev,
        [dayCode]: response.data.workout?.exercises || []
      }));
    } catch (error) {
      console.error("Error fetching session exercises:", error);
    } finally {
      setLoadingExercises(prev => ({ ...prev, [dayCode]: false }));
    }
  };

  const toggleExpand = (code, e) => {
    e?.stopPropagation();
    if (expandedSession === code) {
      setExpandedSession(null);
    } else {
      setExpandedSession(code);
      fetchSessionExercises(code);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-28 bg-[#121212] animate-pulse rounded-2xl" />
        <div className="h-52 bg-[#121212] animate-pulse rounded-2xl" />
        <div className="h-52 bg-[#121212] animate-pulse rounded-2xl" />
      </div>
    );
  }

  if (!programData?.program) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-24 h-24 rounded-2xl bg-[#1A1A1A] flex items-center justify-center mb-5">
          <Dumbbell className="w-12 h-12 text-[#444]" />
        </div>
        <h2 className="text-2xl font-heading font-bold text-white mb-2">Sin programa</h2>
        <p className="text-[#666] max-w-sm">
          Aún no tienes un programa asignado.
        </p>
      </div>
    );
  }

  const { program, current_week } = programData;

  // Get workout days from the fetched cycle data
  const workoutDays = programData?.workout_days || [];
  
  // Calculate global progress
  const totalWeeks = 8;
  const weekProgress = Math.round((current_week / totalWeeks) * 100);

  const getProgressionInfo = (week) => {
    if (week <= 2) return { reps: "12-15", intensity: "Baja", color: "text-[#22C55E]", bg: "bg-[#22C55E]" };
    if (week <= 4) return { reps: "10-12", intensity: "Media", color: "text-[#D4AF37]", bg: "bg-[#D4AF37]" };
    if (week <= 6) return { reps: "8-12", intensity: "Alta", color: "text-orange-400", bg: "bg-orange-400" };
    return { reps: "6-10", intensity: "Máxima", color: "text-red-400", bg: "bg-red-400" };
  };

  const currentProgression = getProgressionInfo(current_week);

  // Session Preview Modal
  const PreviewModal = () => {
    if (!previewSession) return null;
    
    const exercises = sessionExercises[previewSession.code] || [];
    const isLoading = loadingExercises[previewSession.code];
    
    const modalContent = (
      <div 
        className="fixed inset-0 z-[100] flex items-end justify-center bg-black/80"
        onClick={() => setPreviewSession(null)}
      >
        <div 
          className="w-full max-w-lg bg-[#121212] rounded-t-3xl border-t border-[#2A2A2A] max-h-[85vh] flex flex-col animate-slide-up"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Handle bar */}
          <div className="w-12 h-1 bg-[#3A3A3A] rounded-full mx-auto mt-4 mb-2 flex-shrink-0" />
          
          {/* Header */}
          <div className="p-4 border-b border-[#2A2A2A] flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/20 flex items-center justify-center">
                  <span className="text-2xl font-heading font-black text-[#D4AF37]">
                    {previewSession.code}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-heading font-bold text-white">
                    {previewSession.name || `Día ${previewSession.code}`}
                  </h2>
                  <p className="text-[#888] text-sm">
                    Sesión del programa
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setPreviewSession(null)}
                className="p-2 hover:bg-[#2A2A2A] rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-[#888]" />
              </button>
            </div>
          </div>
          
          {/* Exercise list */}
          <ScrollArea className="flex-1 p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : exercises.length > 0 ? (
              <div className="space-y-2">
                {exercises.map((ex, idx) => (
                  <div 
                    key={ex.id || idx}
                    className="p-3 rounded-xl border bg-[#1A1A1A] border-[#2A2A2A]"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold bg-[#2A2A2A] text-[#888]">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-white">
                          {ex.name}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-[#888]">
                          <span className="text-[#D4AF37]">
                            {ex.sets} × {ex.reps}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {ex.rest_seconds}s
                          </span>
                        </div>
                      </div>
                      {ex.superset_group && (
                        <span className="px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] text-[10px] font-bold rounded">
                          SS
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-[#666] py-8">No hay ejercicios</p>
            )}
          </ScrollArea>
          
          {/* Footer */}
          <div className="p-4 border-t border-[#2A2A2A] flex-shrink-0">
            <button
              onClick={() => setPreviewSession(null)}
              className="w-full py-4 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white font-semibold rounded-xl transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
    
    return createPortal(modalContent, document.body);
  };

  return (
    <div className="space-y-6 animate-fade-in pb-8" data-testid="programa-page">
      {/* Program Header */}
      <Card className="bg-[#121212] border-[#2A2A2A]">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <Badge className="mb-2 bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30 hover:bg-[#D4AF37]/20">
                {program?.track === "men" ? "HOMBRES" : "MUJERES"}
              </Badge>
              <h1 className="text-xl font-heading font-bold text-white">{program?.name}</h1>
              <p className="text-[#666] text-sm mt-1 flex items-center gap-2">
                <RotateCcw className="w-4 h-4" />
                Programa de 8 semanas
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-[#666] uppercase tracking-wider">Semana</p>
              <p className="text-4xl font-heading font-black text-[#D4AF37]">{current_week}/8</p>
            </div>
          </div>
          
          {/* Week progress bar */}
          <div className="mt-4">
            <div className="h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] rounded-full transition-all duration-500"
                style={{ width: `${weekProgress}%` }}
              />
            </div>
            <p className="text-xs text-[#666] mt-2 text-center">
              {weekProgress}% del programa completado
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Current Intensity Phase */}
      <Card className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border-[#D4AF37]/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", `${currentProgression.bg}/20`)}>
                <Zap className={cn("w-5 h-5", currentProgression.color)} />
              </div>
              <div>
                <p className="text-white font-semibold">Fase actual</p>
                <p className="text-[#888] text-sm">Semanas {current_week <= 2 ? "1-2" : current_week <= 4 ? "3-4" : current_week <= 6 ? "5-6" : "7-8"}</p>
              </div>
            </div>
            <div className="text-right">
              <p className={cn("font-bold", currentProgression.color)}>{currentProgression.intensity}</p>
              <p className="text-[#666] text-sm">{currentProgression.reps} reps</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Program Structure - Sessions */}
      <div>
        <h2 className="text-lg font-heading font-bold text-white mb-4 flex items-center gap-2">
          <Dumbbell className="w-5 h-5 text-[#D4AF37]" />
          Estructura del Programa
        </h2>
        
        <p className="text-[#666] text-sm mb-4">
          Estas son las sesiones de tu programa. Entrena en orden, cuando tú quieras.
        </p>
        
        <div className="space-y-3">
          {workoutDays.map((day, index) => {
            const isExpanded = expandedSession === day.code;
            const exercises = sessionExercises[day.code] || [];
            const isLoadingEx = loadingExercises[day.code];
            
            return (
              <Card
                key={day.code || index}
                className="bg-[#121212] border-[#2A2A2A] overflow-hidden"
                data-testid={`program-session-${day.code}`}
              >
                <CardContent className="p-0">
                  <button
                    onClick={() => {
                      setPreviewSession(day);
                      fetchSessionExercises(day.code);
                    }}
                    className="w-full flex items-center p-4 active:scale-[0.99] transition-transform"
                  >
                    {/* Session indicator */}
                    <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/20 border border-[#D4AF37]/30 flex flex-col items-center justify-center mr-4 flex-shrink-0">
                      <span className="text-[10px] uppercase text-[#D4AF37] font-semibold">Sesión</span>
                      <span className="text-xl font-heading font-black text-[#D4AF37]">{day.code}</span>
                    </div>
                    
                    {/* Session info */}
                    <div className="flex-1 text-left min-w-0">
                      <h3 className="font-heading font-bold text-white truncate">
                        {day.name || `Día ${day.code}`}
                      </h3>
                      <p className="text-[#666] text-sm flex items-center gap-2">
                        <Dumbbell className="w-3 h-3" />
                        <span>{day.exercise_count || "?"} ejercicios</span>
                      </p>
                    </div>
                    
                    {/* Expand button */}
                    <button
                      onClick={(e) => toggleExpand(day.code, e)}
                      className={cn(
                        "p-2 rounded-lg transition-colors ml-2",
                        isExpanded ? "bg-[#2A2A2A]" : "hover:bg-[#1A1A1A]"
                      )}
                    >
                      <ChevronDown className={cn(
                        "w-5 h-5 text-[#666] transition-transform",
                        isExpanded && "rotate-180"
                      )} />
                    </button>
                  </button>
                  
                  {/* Expanded exercise preview */}
                  {isExpanded && (
                    <div className="border-t border-[#2A2A2A] bg-[#0B0B0B]/50 p-3">
                      {isLoadingEx ? (
                        <div className="flex items-center justify-center py-4">
                          <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : exercises.length > 0 ? (
                        <div className="space-y-2">
                          {exercises.slice(0, 5).map((ex, idx) => (
                            <div 
                              key={ex.id || idx}
                              className="flex items-center gap-3 p-2 rounded-lg bg-[#1A1A1A]/50"
                            >
                              <span className="w-6 h-6 rounded text-xs font-bold flex items-center justify-center bg-[#2A2A2A] text-[#666]">
                                {idx + 1}
                              </span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-white truncate">{ex.name}</p>
                                <p className="text-xs text-[#666]">{ex.sets}×{ex.reps}</p>
                              </div>
                            </div>
                          ))}
                          {exercises.length > 5 && (
                            <p className="text-center text-[#666] text-xs py-1">
                              +{exercises.length - 5} ejercicios más
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-center text-[#666] text-sm py-2">Sin ejercicios</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Progression Info */}
      <Card className="bg-[#121212] border-[#2A2A2A]">
        <CardContent className="p-5">
          <h3 className="font-heading text-white font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
            Sistema de Progresión
          </h3>
          <p className="text-[#666] text-sm mb-4">
            La intensidad aumenta cada 2 semanas para maximizar ganancias.
          </p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className={cn(
              "p-3 rounded-xl border",
              current_week <= 2 ? "bg-[#22C55E]/10 border-[#22C55E]/30" : "bg-[#1A1A1A] border-[#2A2A2A]"
            )}>
              <div className="flex items-center gap-2">
                <p className="text-[#22C55E] font-bold">Semanas 1-2</p>
                {current_week <= 2 && <Badge className="bg-[#22C55E] text-xs px-1.5 py-0">Actual</Badge>}
              </div>
              <p className="text-[#666]">12-15 reps • RIR 2-3</p>
            </div>
            <div className={cn(
              "p-3 rounded-xl border",
              current_week > 2 && current_week <= 4 ? "bg-[#D4AF37]/10 border-[#D4AF37]/30" : "bg-[#1A1A1A] border-[#2A2A2A]"
            )}>
              <div className="flex items-center gap-2">
                <p className="text-[#D4AF37] font-bold">Semanas 3-4</p>
                {current_week > 2 && current_week <= 4 && <Badge className="bg-[#D4AF37] text-[#0B0B0B] text-xs px-1.5 py-0">Actual</Badge>}
              </div>
              <p className="text-[#666]">10-12 reps • RIR 2</p>
            </div>
            <div className={cn(
              "p-3 rounded-xl border",
              current_week > 4 && current_week <= 6 ? "bg-orange-500/10 border-orange-500/30" : "bg-[#1A1A1A] border-[#2A2A2A]"
            )}>
              <div className="flex items-center gap-2">
                <p className="text-orange-400 font-bold">Semanas 5-6</p>
                {current_week > 4 && current_week <= 6 && <Badge className="bg-orange-500 text-xs px-1.5 py-0">Actual</Badge>}
              </div>
              <p className="text-[#666]">8-12 reps • RIR 1-2</p>
            </div>
            <div className={cn(
              "p-3 rounded-xl border",
              current_week > 6 ? "bg-red-500/10 border-red-500/30" : "bg-[#1A1A1A] border-[#2A2A2A]"
            )}>
              <div className="flex items-center gap-2">
                <p className="text-red-400 font-bold">Semanas 7-8</p>
                {current_week > 6 && <Badge className="bg-red-500 text-xs px-1.5 py-0">Actual</Badge>}
              </div>
              <p className="text-[#666]">6-10 reps • RIR 1</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Info banner */}
      <div className="p-4 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A]">
        <p className="text-[#888] text-sm text-center">
          <span className="text-[#D4AF37] font-semibold">Flexibilidad total.</span> No hay días fijos. Entrena las sesiones en orden cuando mejor te venga.
        </p>
      </div>

      {/* Preview Modal */}
      <PreviewModal />
    </div>
  );
};

export default ProgramaPage;
